// 📱 기기 카테고리 판별 유틸리티
// 중고 디지털 기기 매입 프로세스에서 카테고리별 처리 분기를 위한 함수들

import { DeviceInfo } from '../pages/admin/types/application';

/**
 * 📱 기기 타입이 스마트폰인지 확인
 * @param deviceType 기기 타입 문자열
 * @returns 스마트폰 여부
 */
export const isSmartphone = (deviceType: string): boolean => {
  const smartphoneKeywords = [
    '아이폰', 'iPhone', 'iphone',
    '갤럭시', '갤럭시S', '갤럭시노트', '갤럭시Z',
    'Galaxy', 'galaxy',
    '스마트폰', '휴대폰', '핸드폰',
    'smartphone', 'phone'
  ];
  
  return smartphoneKeywords.some(keyword => 
    deviceType.toLowerCase().includes(keyword.toLowerCase())
  );
};

/**
 * 📱 기기 타입이 태블릿인지 확인
 * @param deviceType 기기 타입 문자열
 * @returns 태블릿 여부
 */
export const isTablet = (deviceType: string): boolean => {
  const tabletKeywords = [
    '아이패드', 'iPad', 'ipad',
    '갤럭시탭', 'Galaxy Tab', 'galaxy tab',
    '태블릿', 'tablet',
    '패드', 'pad'
  ];
  
  return tabletKeywords.some(keyword => 
    deviceType.toLowerCase().includes(keyword.toLowerCase())
  );
};

/**
 * ⌚ 기기 타입이 웨어러블인지 확인
 * @param deviceType 기기 타입 문자열
 * @returns 웨어러블 여부
 */
export const isWearable = (deviceType: string): boolean => {
  const wearableKeywords = [
    '애플워치', 'Apple Watch', 'apple watch',
    '갤럭시워치', 'Galaxy Watch', 'galaxy watch',
    '에어팟', 'AirPods', 'airpods',
    '갤럭시버즈', 'Galaxy Buds', 'galaxy buds',
    '워치', 'watch',
    '이어폰', '헤드폰', 'earphone', 'headphone',
    '웨어러블', 'wearable'
  ];
  
  return wearableKeywords.some(keyword => 
    deviceType.toLowerCase().includes(keyword.toLowerCase())
  );
};

/**
 * 📱 기기 카테고리 분류
 * @param deviceType 기기 타입 문자열
 * @returns 기기 카테고리 ('smartphone' | 'tablet' | 'wearable' | 'other')
 */
export const getDeviceCategory = (deviceType: string): 'smartphone' | 'tablet' | 'wearable' | 'other' => {
  if (isSmartphone(deviceType)) return 'smartphone';
  if (isTablet(deviceType)) return 'tablet';
  if (isWearable(deviceType)) return 'wearable';
  return 'other';
};

/**
 * 📱 신청서의 모든 기기가 스마트폰인지 확인
 * @param devices 기기 배열
 * @returns 모든 기기가 스마트폰인지 여부
 */
export const areAllDevicesSmartphones = (devices: DeviceInfo[]): boolean => {
  if (!devices || devices.length === 0) {
    console.log('🔍 [areAllDevicesSmartphones] 기기 배열이 비어있음');
    return false;
  }
  
  const results = devices.map(device => {
    const isPhone = isSmartphone(device.deviceType);
    console.log(`🔍 [areAllDevicesSmartphones] ${device.deviceType} → 스마트폰: ${isPhone}`);
    return isPhone;
  });
  
  const allSmartphones = results.every(result => result);
  console.log('🔍 [areAllDevicesSmartphones] 모든 기기가 스마트폰:', allSmartphones);
  
  return allSmartphones;
};

/**
 * 📱 신청서에 스마트폰이 포함되어 있는지 확인
 * @param devices 기기 배열
 * @returns 스마트폰 포함 여부
 */
export const hasSmartphone = (devices: DeviceInfo[]): boolean => {
  if (!devices || devices.length === 0) return false;
  return devices.some(device => isSmartphone(device.deviceType));
};

/**
 * 📱 신청서의 기기 카테고리별 개수 반환
 * @param devices 기기 배열
 * @returns 카테고리별 개수 객체
 */
export const getDeviceCategoryCounts = (devices: DeviceInfo[]) => {
  const counts = {
    smartphone: 0,
    tablet: 0,
    wearable: 0,
    other: 0,
  };

  if (!devices || devices.length === 0) return counts;

  devices.forEach(device => {
    const category = getDeviceCategory(device.deviceType);
    counts[category]++;
  });

  return counts;
};

/**
 * 📱 계약서 기능 사용 가능 여부 확인
 * 스마트폰 또는 태블릿이 포함된 경우 계약서 기능 사용 가능 (혼합 신청도 포함)
 * @param devices 기기 배열
 * @returns 계약서 기능 사용 가능 여부
 */
export const isContractFeatureAvailable = (devices: DeviceInfo[]): boolean => {
  if (!devices || devices.length === 0) return false;
  return devices.some(device => isSmartphone(device.deviceType) || isTablet(device.deviceType));
};

/**
 * 📱 기존 입금 프로세스 사용 여부 확인
 * 태블릿, 웨어러블, 또는 혼합 기기인 경우 기존 프로세스 사용
 * @param devices 기기 배열
 * @returns 기존 입금 프로세스 사용 여부
 */
export const shouldUseLegacyPaymentProcess = (devices: DeviceInfo[]): boolean => {
  const contractAvailable = isContractFeatureAvailable(devices);
  const shouldUseLegacy = !contractAvailable;
  
  console.log('🔍 [deviceCategoryUtils] shouldUseLegacyPaymentProcess 호출');
  console.log('🔍 [deviceCategoryUtils] 입력 기기들:', devices.map(d => ({ id: d.id, deviceType: d.deviceType })));
  console.log('🔍 [deviceCategoryUtils] 계약서 기능 사용 가능:', contractAvailable);
  console.log('🔍 [deviceCategoryUtils] 기존 프로세스 사용:', shouldUseLegacy);
  
  return shouldUseLegacy;
};

/**
 * 📱 기기 카테고리별 프로세스 안내 메시지
 * @param devices 기기 배열
 * @returns 프로세스 안내 메시지
 */
export const getProcessMessage = (devices: DeviceInfo[]): string => {
  const categoryCounts = getDeviceCategoryCounts(devices);
  
  if (categoryCounts.smartphone > 0 || categoryCounts.tablet > 0) {
    return '스마트폰 또는 태블릿이 포함된 신청은 매매 계약서 프로세스로 진행됩니다.';
  }
  
  if (categoryCounts.wearable > 0) {
    return '웨어러블 기기는 기존 입금 프로세스로 진행됩니다.';
  }
  
  return '기존 입금 프로세스로 진행됩니다.';
};
